// Флаш очереди правок каталога на сервер (фаза 3, этап B).
//
// Как и кухонный `syncEngine`: синхронизация ТОЛЬКО по инициативе клиента (событие
// `online`, постановка в очередь, ручной retry), один флаш за раз, путь до сервера
// зашит здесь строкой, а не тянется из `modules/catalog` (слой ядра не зависит от
// модулей). Идемпотентность приёма — по `clientId`: сервер отдаёт уже созданную
// запись вместо ошибки на повторе.

import { HttpError, NetworkError, httpClient } from '@/shared/api';

import { offlineDb, type CabinetOp } from './db';
import { pendingCabinetOps } from './cabinetQueue';

const CATALOG = '/catalog';
const TENANTS = '/tenants';

export type CabinetFlushResult = {
  readonly sent: number;
  readonly failed: number;
  readonly remaining: number;
};

let inFlight: Promise<CabinetFlushResult> | null = null;

/** Пометки «запись изменилась на сервере, пока правили офлайн» — id → true. */
const concurrentChanges = new Map<string, true>();

export function hasConcurrentChange(id: string): boolean {
  return concurrentChanges.has(id);
}

export function acknowledgeConcurrentChange(id: string): void {
  concurrentChanges.delete(id);
}

export function listConcurrentChanges(): string[] {
  return [...concurrentChanges.keys()];
}

/**
 * Отправить очередь кабинета. Возвращает итог; несколько одновременных вызовов
 * разделяют один проход (иначе оба снимают один список до пометки `syncing` и шлют
 * дубли — B-008).
 */
export function flushCabinetQueue(): Promise<CabinetFlushResult> {
  if (inFlight) {
    return inFlight;
  }
  inFlight = run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run(): Promise<CabinetFlushResult> {
  const ops = await pendingCabinetOps();
  let sent = 0;
  let failed = 0;

  for (const op of ops) {
    await offlineDb.cabinetQueue.update(op.clientId, { status: 'syncing' });
    try {
      await sendOne(op);
      await offlineDb.cabinetQueue.delete(op.clientId);
      sent += 1;
    } catch (error) {
      if (error instanceof NetworkError) {
        // Сеть пропала посреди флаша — операция цела, вернётся при следующем проходе.
        await offlineDb.cabinetQueue.update(op.clientId, { status: 'pending' });
        break;
      }
      if (error instanceof HttpError && (error.status === 401 || error.status === 403)) {
        // Сервер не признал субъекта: сессия истекла. Операция исправна — вернуть
        // в `pending` и прервать флаш, следующая упёрлась бы в тот же отказ.
        await offlineDb.cabinetQueue.update(op.clientId, { status: 'pending' });
        break;
      }
      if (error instanceof HttpError && error.status === 404 && op.action === 'delete') {
        // Запись уже удалена (или заархивирована) — цель достигнута, повтор бессмыслен.
        await offlineDb.cabinetQueue.delete(op.clientId);
        sent += 1;
        continue;
      }
      // Сервер рассмотрел и отказал (валидация, доступ). Повтор даст тот же ответ —
      // операция ждёт решения человека, флаш продолжается со следующей.
      failed += 1;
      await offlineDb.cabinetQueue.update(op.clientId, {
        status: 'error',
        attempts: op.attempts + 1,
        lastError: describe(error),
      });
    }
  }

  return { sent, failed, remaining: await offlineDb.cabinetQueue.count() };
}

async function sendOne(op: CabinetOp): Promise<void> {
  switch (`${op.entity}:${op.action}`) {
    case 'category:create':
      await httpClient.post(`${CATALOG}/categories/`, { ...op.payload, id: op.clientId });
      return;
    case 'category:update':
      await patchWithConflict(`${CATALOG}/categories/${op.targetId}/`, op);
      return;
    case 'category:delete':
      await httpClient.delete(`${CATALOG}/categories/${op.targetId}/`);
      return;

    case 'masterProduct:create':
      await httpClient.post(`${CATALOG}/master-products/`, { ...op.payload, id: op.clientId });
      return;
    case 'masterProduct:update':
      await patchWithConflict(`${CATALOG}/master-products/${op.targetId}/`, op);
      return;
    case 'masterProduct:delete':
      await httpClient.delete(`${CATALOG}/master-products/${op.targetId}/`);
      return;

    case 'locationProduct:create':
      await httpClient.post(
        `${CATALOG}/location-products/`,
        { ...op.payload, id: op.clientId },
        { searchParams: { location: op.locationId ?? '' } },
      );
      return;
    case 'locationProduct:update':
      await patchWithConflict(`${CATALOG}/location-products/${op.targetId}/`, op);
      return;
    case 'locationProduct:delete':
      // Сервер трактует DELETE как архивацию (idempotent) — повтор безопасен.
      await httpClient.delete(`${CATALOG}/location-products/${op.targetId}/`);
      return;

    case 'locationProduct:addFromCatalog':
      // Точка — из самой операции, как у онлайн-пути (`catalogApi.addFromCatalog`): без неё
      // сервер отвечает 400, и повтор уходил в тот же отказ (B-040).
      await httpClient.post(
        `${CATALOG}/location-products/add-from-catalog/`,
        { master_product_ids: op.payload.masterProductIds },
        { searchParams: { location: op.locationId ?? '' } },
      );
      return;

    case 'staff:update':
      await patchWithConflict(`${TENANTS}/users/${op.targetId}/`, op);
      return;
    case 'staff:toggleBlock': {
      // payload.blocked — состояние ДО переключения (как в useToggleBlock):
      // true уже заблокирован → зовём unblock, false активен → block.
      const endpoint = op.payload.blocked ? 'unblock' : 'block';
      await httpClient.post(`${TENANTS}/users/${op.targetId}/${endpoint}/`);
      return;
    }

    default: {
      const exhaustive = `${op.entity}:${op.action}`;
      throw new Error(`Неизвестная операция кабинета: ${exhaustive}`);
    }
  }
}

/**
 * PATCH с обнаружением конкурентной правки.
 *
 * `base_updated_at` — состояние записи на момент начала правки. Сервер применяет
 * PATCH в любом случае (last-write-wins, решение владельца), но если запись
 * менялась в промежутке — помечаем её локально, и интерфейс покажет
 * «изменено на сервере, проверьте».
 */
async function patchWithConflict(path: string, op: CabinetOp): Promise<void> {
  const body = op.baseUpdatedAt ? { ...op.payload, base_updated_at: op.baseUpdatedAt } : op.payload;
  const result = (await httpClient.patch(path, body)) as { concurrent_change?: boolean };
  if (result?.concurrent_change && op.targetId) {
    concurrentChanges.set(op.targetId, true);
  }
}

function describe(error: unknown): string {
  if (error instanceof HttpError) {
    const payload = error.payload as { error?: { message?: string } } | undefined;
    return payload?.error?.message ?? `Сервер отклонил правку (${error.status}).`;
  }
  return 'Не удалось отправить правку.';
}
